import { useEffect, useRef } from "react";
import type { FieldErrors, FieldValues, UseFormReturn } from "react-hook-form";

import type { Error } from "@auth0/auth0-acul-js";

/**
 * Maps SDK error field names to form field names
 * This helps us find the correct form field when SDK returns errors
 */
const SDK_FIELD_NAME_MAP: Record<string, string[]> = {
  username: ["username"],
  email: ["username", "identifier"],
  phone: ["identifier"],
  password: ["password"],
  "password-reset": ["password"],
  "re-enter-password": ["passwordConfirmation"],
  confirmPassword: ["confirmPassword"],
  captcha: ["captcha"],
  identifier: ["identifier"],
};

/**
 * Default priority order for common form fields
 * Fields earlier in this array will be focused first when multiple errors exist
 */
const DEFAULT_FIELD_PRIORITY = [
  "username",
  "identifier",
  "email",
  "password",
  "confirmPassword",
  "passwordConfirmation",
  "termsAccepted",
  "captcha",
];

/**
 * Gets the form field name from an SDK error field name
 */
function getFormFieldName(sdkFieldName: string): string | null {
  const mappedFields = SDK_FIELD_NAME_MAP[sdkFieldName];
  return mappedFields?.[0] || null;
}

/**
 * Gets the default field order based on available fields and priority
 */
function getDefaultFieldOrder<T extends FieldValues>(
  formErrors: FieldErrors<T>,
  sdkErrors: Error[] | undefined
): (keyof T)[] {
  // Collect all fields that have errors
  const errorFields = new Set<keyof T>();

  // Add fields from form errors
  Object.keys(formErrors).forEach((key) => {
    errorFields.add(key as keyof T);
  });

  // Add fields from SDK errors
  if (sdkErrors) {
    sdkErrors.forEach((error) => {
      if (error.field) {
        const formFieldName = getFormFieldName(error.field);
        if (formFieldName) {
          errorFields.add(formFieldName as keyof T);
        }
      }
    });
  }

  // Sort by default priority, then alphabetically for any remaining fields
  const fieldArray = Array.from(errorFields);
  return fieldArray.sort((a, b) => {
    const aPriority = DEFAULT_FIELD_PRIORITY.indexOf(
      String(a) as (typeof DEFAULT_FIELD_PRIORITY)[number]
    );
    const bPriority = DEFAULT_FIELD_PRIORITY.indexOf(
      String(b) as (typeof DEFAULT_FIELD_PRIORITY)[number]
    );

    // If both are in priority list, sort by priority
    if (aPriority !== -1 && bPriority !== -1) {
      return aPriority - bPriority;
    }
    // If only one is in priority list, prioritize it
    if (aPriority !== -1) return -1;
    if (bPriority !== -1) return 1;
    // Otherwise, sort alphabetically
    return String(a).localeCompare(String(b));
  });
}

/**
 * Finds the first field with an error (either form validation or SDK error)
 * Returns the field name in the order they appear in the form
 */
function findFirstErrorField<T extends FieldValues>(
  formErrors: FieldErrors<T>,
  sdkErrors: Error[] | undefined,
  fieldOrder?: (keyof T)[]
): keyof T | null {
  // Use provided fieldOrder or generate default based on errors
  const order = fieldOrder || getDefaultFieldOrder(formErrors, sdkErrors);

  // First, check SDK errors in field order
  if (sdkErrors && sdkErrors.length > 0) {
    for (const fieldName of order) {
      const sdkError = sdkErrors.find((error) => {
        if (!error.field) return false;
        const formFieldName = getFormFieldName(error.field);
        return formFieldName === fieldName;
      });
      if (sdkError) {
        return fieldName;
      }
    }
  }

  // Then check form validation errors in field order
  for (const fieldName of order) {
    if (formErrors[fieldName]) {
      return fieldName;
    }
  }

  return null;
}

/**
 * Focuses on the first field with an error in the form
 * Uses react-hook-form's setFocus method for reliable focusing
 *
 * @param form - The react-hook-form instance
 * @param sdkErrors - Optional array of SDK errors
 * @param fieldOrder - Optional array of field names in the order they appear in the form.
 *                     If not provided, uses a default priority order based on common field names.
 */
export function focusFirstErrorField<T extends FieldValues>(
  form: UseFormReturn<T>,
  sdkErrors: Error[] | undefined,
  fieldOrder?: (keyof T)[]
): void {
  const formErrors = form.formState.errors;
  const firstErrorField = findFirstErrorField(
    formErrors,
    sdkErrors,
    fieldOrder
  );

  if (firstErrorField) {
    try {
      // Use react-hook-form's setFocus for reliable focusing
      // Since this is called from useEffect, the DOM is already updated
      form.setFocus(firstErrorField as Parameters<typeof form.setFocus>[0], {
        shouldSelect: false,
      });
    } catch (error) {
      // Fallback to manual focus if setFocus fails
      console.warn("Failed to focus field using setFocus:", error);
      const fieldName = String(firstErrorField);
      const inputElement = document.querySelector(
        `[name="${fieldName}"], [id*="${fieldName}"]`
      ) as HTMLInputElement | null;

      if (inputElement) {
        inputElement.focus();
        // Scroll into view if needed
        inputElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }
}

/**
 * Hook to automatically focus on the first error field when form validation fails or SDK errors appear
 *
 * @param form - The react-hook-form instance
 * @param sdkErrors - Optional array of SDK errors
 * @param fieldOrder - Optional array of field names in the order they appear in the form.
 *                     If not provided, uses a default priority order based on common field names.
 */
export function useFocusFirstError<T extends FieldValues>(
  form: UseFormReturn<T>,
  sdkErrors: Error[] | undefined,
  fieldOrder?: (keyof T)[]
): void {
  const hasFocusedRef = useRef<string>("");

  useEffect(() => {
    const formErrors = form.formState.errors;
    const hasFormErrors = Object.keys(formErrors).length > 0;
    const hasSDKErrors = sdkErrors && sdkErrors.length > 0;

    if (hasFormErrors || hasSDKErrors) {
      // Create a unique key from the error state to detect when errors change
      const errorKey = JSON.stringify({
        formErrors: Object.keys(formErrors).sort(),
        sdkErrors: sdkErrors
          ?.map((e) => e.field)
          .filter(Boolean)
          .sort(),
      });

      // Only focus if this is a new error state (not the same errors we've already focused on)
      if (hasFocusedRef.current !== errorKey) {
        focusFirstErrorField(form, sdkErrors, fieldOrder);
        hasFocusedRef.current = errorKey;
      }
    } else {
      // Reset focus flag when form becomes valid or errors clear
      hasFocusedRef.current = "";
    }
  }, [form.formState.errors, sdkErrors, form, fieldOrder]);
}
